import * as React from 'react';
import { Mutation } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { DataProxy } from 'apollo-cache';

import ToastActions from 'actions/ToastActions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import Dialog from 'components/Dialog';

import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';

import ADMIN_SCHOOL_SIS_DASHBOARD_QUERY from 'admin/rostering/queries/adminSchoolSisDashboardQuery.gql';
import RESUME_ROSTERING_MUTATION from 'admin//rostering/mutations/ResumeRosteringMutation.gql';
import {
  AdminSchoolSisDashboard,
  AdminSchoolSisDashboard_organizations,
} from 'src/admin/rostering/queries/__generated__/AdminSchoolSisDashboard';
import {
  resumeRosteringVariables,
  resumeRostering_resumeRostering as resumeRosteringResult,
  resumeRostering as resumeRosteringData,
} from 'admin/rostering/mutations/__generated__/resumeRostering';

import './styles/AdminSisSyncHoldViewResumeModal.scss';

type Props = {
  permanentlyIgnoreable: boolean;
  temporarilyIgnored: boolean;
  orgUuid: string;
  holdKey: string;
  syncMethod: string;
  onRequestHide?: () => void;
};

export default class AdminSisSyncHoldViewResumeModal extends React.Component<Props> {
  static defaultProps = {
    permanentlyIgnoreable: false,
    temporarilyIgnored: false,
  };

  handleComplete = (result: resumeRosteringResult) => {
    if (result.error) {
      const { fieldErrors } = result.error;

      const errorMessage =
        fieldErrors && fieldErrors.length
          ? result.error.fieldErrors.map(f => f.message).join(', ')
          : result.error.message;

      ToastActions.showErrorToast(errorMessage);
      console.debug(result.error);
      console.error('AdminSisSyncHoldViewResumeModal - could not resume');
    } else {
      ToastActions.showSuccessToast('Sync resumed');
    }
  };

  handleError = (error: ApolloError) => {
    ToastActions.showErrorToast('Oops, something went wrong resuming the sync');
    console.debug(error, error.stack);
    console.error('AdminSisSyncHoldViewResumeModal - hard fail - could not trigger resume');
  };

  onUpdate = (cache: DataProxy, { data }: { data?: resumeRosteringData }) => {
    const { orgUuid } = this.props;

    if (data && data.resumeRostering.error) {
      return;
    }

    const currentData: AdminSchoolSisDashboard | null = cache.readQuery({
      query: ADMIN_SCHOOL_SIS_DASHBOARD_QUERY,
      variables: { organizationUuid: [orgUuid] },
    });

    if (!currentData) {
      return;
    }

    const organization = findMatchingRecord<AdminSchoolSisDashboard_organizations>(
      currentData.organizations,
      orgUuid
    );

    if (organization && organization.sisSyncOverview) {
      organization.sisSyncOverview.syncStatus = 'processing';
      const index = findMatchingRecordIndex(currentData.organizations, orgUuid);

      if (index) {
        currentData.organizations[index] = organization;
        cache.writeQuery({ query: ADMIN_SCHOOL_SIS_DASHBOARD_QUERY, data: currentData });
      }
    }
  };

  renderActionDialog = (title: string, subtext: string) => {
    const {
      permanentlyIgnoreable,
      onRequestHide,
      temporarilyIgnored,
      holdKey,
      orgUuid,
    } = this.props;
    const skipPermanently = !!(this.refs.checkbox && (this.refs.checkbox as Checkbox).isChecked());
    const skipUntilSuccessful = !!temporarilyIgnored;

    return (
      <Mutation
        mutation={RESUME_ROSTERING_MUTATION}
        onCompleted={this.handleComplete}
        onError={this.handleError}
        update={this.onUpdate}>
        {(resumeRostering: (input: { variables: resumeRosteringVariables }) => void) => (
          <ConfirmDialog
            title={title}
            subtext={subtext}
            onRequestHide={onRequestHide}
            actionButtonText="Yes"
            cancelButtonText="Cancel"
            className="AdminSisSyncHoldViewResumeModal"
            preConfirmPromise={() =>
              Promise.resolve(
                resumeRostering({
                  variables: {
                    input: {
                      orgUuid,
                      skipPermanently,
                      skipUntilSuccessful,
                      holdKey,
                    },
                  },
                })
              )
            }>
            {permanentlyIgnoreable && (
              <div className="AdminSisSyncHoldViewResumeModal-checkbox">
                <Checkbox
                  flush
                  label="Don’t hold syncs like this again"
                  name="skip"
                  ref="checkbox"
                />
              </div>
            )}
          </ConfirmDialog>
        )}
      </Mutation>
    );
  };

  renderInformationalDialog = (title: string, subtext: string) => {
    const { onRequestHide } = this.props;

    return (
      <Dialog title={title} subtext={subtext} onRequestHide={onRequestHide} size="xsmall">
        <BoxBody>
          <Button level="secondary" onClick={onRequestHide} full>
            Got it
          </Button>
        </BoxBody>
      </Dialog>
    );
  };

  render() {
    const { temporarilyIgnored, syncMethod } = this.props;
    const title = temporarilyIgnored
      ? 'Ignore the hold and continue?'
      : 'Did you fix the data errors?';
    const subtext = temporarilyIgnored
      ? 'If you continue, we’ll resume the sync with the data changes we initially detected.'
      : 'We’ll start processing your data as soon as we receive it. If you’ve made changes, we’ll see them.';

    return temporarilyIgnored || syncMethod === 'Clever'
      ? this.renderActionDialog(title, subtext)
      : this.renderInformationalDialog(title, subtext);
  }
}
